import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from '../drawer/context';
import type { DrawerTarget } from '../drawer/content';
import { LaneRow, Pill, Stat } from '../primitives';
import type { Connector, PostureResponse, PostureStats, TriageRow } from '../../lib/types';

export type Direction = 'a' | 'b';
export const DIR_KEY = 'cyberwise-dir';

export function readDirection(): Direction {
  try {
    return localStorage.getItem(DIR_KEY) === 'b' ? 'b' : 'a';
  } catch {
    return 'a'; // storage disabled — fall back, never crash the shell
  }
}

export function writeDirection(d: Direction): void {
  try {
    localStorage.setItem(DIR_KEY, d);
  } catch {
    /* preference simply does not persist */
  }
}

export function DirectionSwitch({
  value,
  onChange,
}: {
  value: Direction;
  onChange: (d: Direction) => void;
}) {
  return (
    <div className="dirsw">
      <span className="dl">Landing</span>
      <button className={`db1${value === 'a' ? ' on' : ''}`} onClick={() => onChange('a')} type="button">
        A · Connector board
      </button>
      <button className={`db1${value === 'b' ? ' on' : ''}`} onClick={() => onChange('b')} type="button">
        B · Triage stream
      </button>
    </div>
  );
}

function SectionHeading({ title, count, note }: { title: string; count?: number; note?: string }) {
  return (
    <div className="sec-h">
      <h2>{title}</h2>
      {count !== undefined && <span className="c">{count}</span>}
      {note && <span className="sp">{note}</span>}
    </div>
  );
}

function Bar({ pct, tone }: { pct: number | null; tone: string }) {
  return (
    <span className="pwrap">
      <span className="bar">
        <i className={tone} style={{ width: `${pct ?? 0}%` }} />
      </span>
      <span>{pct === null ? '—' : `${pct}%`}</span>
    </span>
  );
}

const relSync = (iso: string | null): string => {
  if (!iso) return 'never';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'synced just now';
  if (mins < 60) return `synced ${mins} min ago`;
  const h = Math.floor(mins / 60);
  return `synced ${h}h ${mins % 60}m ago`;
};

function ConnectorCard({ c, onOpen }: { c: Connector; onOpen: () => void }) {
  const pill =
    c.state === 'healthy' ? (
      <Pill kind="ok">healthy</Pill>
    ) : c.state === 'degraded' ? (
      <Pill kind="warn">degraded</Pill>
    ) : (
      <Pill kind="neutral">not connected</Pill>
    );

  return (
    <div className="tool" onClick={onOpen} role="button">
      <div className="th">
        <span className="glyph" style={{ background: c.color }}>
          {c.short}
        </span>
        <span>
          <span className="tn">{c.name}</span>
          <span className="tv">{c.vendor}</span>
        </span>
        <span style={{ marginLeft: 'auto' }}>{pill}</span>
      </div>

      {c.state === 'not_connected' ? (
        /* No metric rows at all — not a column of zeros.
           "0 open detections" and "we cannot see detections" look identical on
           a board and mean opposite things. On a security screen a confident
           zero that actually means "no credentials" is the most dangerous cell
           there is, so this state shows the reason instead of numbers. */
        <div className="cw-nc">
          <span className="cw-nc-t">Not reporting</span>
          <span className="cw-nc-r">{c.reason}</span>
        </div>
      ) : (
        <>
          <div className="tb">
            {c.metrics.map((m) => (
              <div className="mrow" key={m.l}>
                <span className="ml">{m.l}</span>
                <span className={`mv ${m.tone}`}>
                  {m.v === null ? <span className="cw-unread">—</span> : m.v}
                </span>
              </div>
            ))}
          </div>
          <div className="tf">
            <span>{c.coverage ?? 'coverage unavailable'}</span>
            <span>{c.state === 'degraded' ? (c.reason ?? 'stale') : relSync(c.syncedAt)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function FrameworkCard({
  fw,
  onOpen,
}: {
  fw: PostureResponse['frameworks'][number];
  onOpen: () => void;
}) {
  const pct = fw.percentMet;
  const tone = pct === null ? '' : pct > 85 ? 'g' : pct > 65 ? 'w' : 'r';
  const gapPill =
    fw.total === 0 ? (
      <Pill kind="neutral">no controls</Pill>
    ) : fw.gaps === 0 ? (
      <Pill kind="ok">clean</Pill>
    ) : (
      <Pill kind={fw.gaps > 2 ? 'bad' : 'warn'}>
        {fw.gaps} gap{fw.gaps > 1 ? 's' : ''}
      </Pill>
    );

  return (
    <div className="fw" onClick={onOpen} role="button">
      <div className="fh">
        <span>
          <span className="fn">{fw.framework}</span>
          <span className="fs">{fw.total === 0 ? 'not yet loaded' : `${fw.total} controls tracked`}</span>
        </span>
        {gapPill}
      </div>
      <div className="ring">
        <span>
          {/* A framework with no controls is UNKNOWN, not 0% compliant. */}
          <span className="pct">{pct === null ? '—' : `${pct}%`}</span>
          <div className="pl">controls met</div>
        </span>
        <span style={{ flex: 1 }}>
          <Bar pct={pct} tone={tone} />
        </span>
      </div>
      <div className="fmeta">
        <span>{fw.total} controls</span>
        <span>
          {fw.nextReview
            ? `next: ${new Date(fw.nextReview).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
            : 'next: —'}
        </span>
      </div>
    </div>
  );
}

const KIND_LABEL: Record<TriageRow['kind'], string> = {
  detection: 'Detections',
  control: 'Control gaps',
  deadline: 'Overdue',
  request: 'Intake',
  vendor: 'Vendors',
};

/**
 * Names the sources we cannot see.
 *
 * Without this an empty lane reads as "nothing needs an analyst" — a false
 * all-clear, and the single worst thing this screen could say.
 */
/**
 * Says which sources the lane below cannot speak for. Two different gaps, two
 * sentences: a source that is not reporting at all, and one that reports
 * counts but whose individual detections are not listed here yet. Either way
 * an empty lane is NOT a clean bill of health, and the banner says so.
 */
function BlindBanner({ blind, countsOnly, total }: { blind: string[]; countsOnly: string[]; total: number }) {
  if (blind.length === 0 && countsOnly.length === 0) return null;
  return (
    <div className="cw-blind">
      <span>
        {blind.length > 0 && (
          <>
            <b>
              {blind.length} of {total} signal sources {blind.length === 1 ? 'is' : 'are'} not reporting.
            </b>{' '}
            {blind.join(', ')}.{' '}
          </>
        )}
        {countsOnly.length > 0 && (
          <>
            <b>{countsOnly.join(', ')}</b> {countsOnly.length === 1 ? 'reports' : 'report'} counts on the connector board, but{' '}
            {countsOnly.length === 1 ? 'its' : 'their'} individual detections are not listed in this lane yet.{' '}
          </>
        )}
        Anything those tools would have raised is not represented below, so this lane is not a complete picture of open
        risk.
      </span>
    </div>
  );
}

function TriageLane({ rows, stats, total }: { rows: TriageRow[]; stats: PostureStats; total: number }) {
  const [kind, setKind] = useState<TriageRow['kind'] | 'all'>('all');
  const openDrawer = useDrawer();

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const r of rows) c[r.kind] = (c[r.kind] ?? 0) + 1;
    return c;
  }, [rows]);

  const shown = kind === 'all' ? rows : rows.filter((r) => r.kind === kind);
  const kinds: (TriageRow['kind'] | 'all')[] = [
    'all',
    'detection',
    'control',
    'deadline',
    'request',
    'vendor',
  ];

  return (
    <>
      <BlindBanner blind={stats.blindSources} countsOnly={stats.countsOnly} total={total} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {kinds.map((k) => (
          <button
            key={k}
            className={`chip${kind === k ? ' on' : ''}`}
            onClick={() => setKind(k)}
            type="button"
          >
            {k === 'all' ? 'All' : KIND_LABEL[k]} <span className="k">{counts[k] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="lane">
        {shown.length === 0 ? (
          <div className="cw-empty">
            <b>Nothing in this lane</b>
            <span>
              {rows.length === 0
                ? `No control gaps, overdue items, breached requests or unreviewed vendors are recorded.${
                    stats.blindSources.length + stats.countsOnly.length > 0
                      ? ' With the sources above not feeding this lane, that reflects what CyberWISE can see, not a quiet program.'
                      : ''
                  }`
                : 'No items of this kind. Clear the filter to see the rest of the lane.'}
            </span>
          </div>
        ) : (
          shown.map((r) => (
            <LaneRow
              key={`${r.kind}-${r.id}`}
              severity={r.severity}
              glyph={r.glyph}
              title={r.title}
              sub={r.sub}
              right={r.right}
              onOpen={
                r.kind === 'detection'
                  ? undefined
                  : () => openDrawer({ kind: r.kind, id: r.id } as DrawerTarget)
              }
            />
          ))
        )}
      </div>
    </>
  );
}

export function Posture({ data, direction }: { data: PostureResponse; direction: Direction }) {
  const navigate = useNavigate();
  const openDrawer = useDrawer();
  const { connectors, stats, frameworks, triage, risk } = data;
  const toFramework = (f: string) => navigate(`/controls?framework=${encodeURIComponent(f)}`);

  const frameworkBand = (
    <div className="mt">
      <SectionHeading title="Framework readiness" note="Clicking a framework opens Controls filtered to it" />
      <div className="fw-grid">
        {frameworks.map((f) => (
          <FrameworkCard key={f.framework} fw={f} onOpen={() => toFramework(f.framework)} />
        ))}
      </div>
    </div>
  );

  const connectorBand = (
    <div className="mt">
      <SectionHeading
        title="Connectors"
        count={connectors.length}
        note={`${connectors.filter((c) => c.state === 'healthy').length} reporting`}
      />
      <div className="tool-grid four">
        {connectors.map((c) => (
          <ConnectorCard key={c.id} c={c} onOpen={() => openDrawer({ kind: 'connector', id: c.id })} />
        ))}
      </div>
    </div>
  );

  if (direction === 'a') {
    return (
      <>
        <div className="stats c5">
          <Stat
            tone="r"
            label="Open critical/high"
            value={stats.openCriticalHigh}
            hint={stats.openCriticalHigh === null ? 'no detection feed connected' : 'across all sources'}
          />
          <Stat tone="w" label="Control gaps" value={stats.controlGaps} hint="across 4 frameworks" />
          <Stat tone="r" label="Overdue items" value={stats.overdue} hint="past their due date" />
          <Stat tone="v" label="Open incidents" value={stats.openIncidents} hint="not yet closed" />
          <Stat label="Intake queue" value={stats.intakeQueue} hint="awaiting a response" />
        </div>

        {connectorBand}

        <div className="mt">
          <SectionHeading
            title="Detections needing an analyst"
            count={triage.filter((r) => r.kind === 'detection').length}
          />
          <TriageLane rows={triage.filter((r) => r.kind === 'detection')} stats={stats} total={connectors.length} />
        </div>

        {frameworkBand}
      </>
    );
  }

  return (
    <>
      <div className="hero">
        <div className="score">
          <div className="k">Program risk index</div>
          {risk.score === null ? (
            <div className="cw-noscore">Not scored</div>
          ) : (
            <div className="big">
              {risk.score}
              <em>/100</em>
            </div>
          )}
          <div className="trend">
            {risk.reason ??
              (risk.score === null ? '' : 'Scored on control health, calendar and connector coverage')}
          </div>
        </div>
        <div className="brk">
          {risk.breakdown.map((b) => (
            <div className="b1" key={b.label}>
              <div className="bl">{b.label}</div>
              <div className="bv">{b.value}</div>
              <div className="bs">{b.sub}</div>
              <div className="bbar">
                <i className={b.tone === 'g' ? '' : b.tone} style={{ width: `${b.pct ?? 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt">
        <SectionHeading
          title="Triage stream"
          count={triage.length}
          note="Every source in one severity-ordered lane"
        />
        <TriageLane rows={triage} stats={stats} total={connectors.length} />
      </div>

      {connectorBand}
      {frameworkBand}
    </>
  );
}
